/**
 * ScreenSizeModeSelector Component
 * 
 * Standardized mode selector for fields that support breakpoint-aware editing.
 * Provides three modes:
 * - 'one' (Unified): Single value applied to all screen sizes
 * - 'two' (Split): Separate values for mobile (XS+S) and desktop (M+L+XL)
 * - 'all' (Custom): Individual control for each breakpoint
 * 
 * Features:
 * - Consistent positioning (label left, buttons right)
 * - Custom SVG icons for visual clarity
 * - Tooltips with explanatory text
 * - Design system typography integration
 * - Disabled state support
 */

import React from 'react';
import { MoreOutlined } from '@ant-design/icons';
import { typography, surfaces, borders, textColors } from '../../../design-system/tokens';

/**
 * Custom SVG icon showing mobile + desktop together
 */
const SplitModeIcon = () => (
  <svg 
    width="16" 
    height="16" 
    viewBox="0 0 16 16" 
    fill="none" 
    xmlns="http://www.w3.org/2000/svg"
    style={{ display: 'inline-block', verticalAlign: 'middle' }}
  >
    {/* Mobile device (left side) */}
    <rect x="1" y="2" width="5" height="9" rx="0.5" stroke="currentColor" strokeWidth="1.2" fill="none"/>
    <line x1="2.5" y1="9.5" x2="4.5" y2="9.5" stroke="currentColor" strokeWidth="0.8"/>
    
    {/* Desktop device (right side) */}
    <rect x="8" y="3" width="7" height="6" rx="0.5" stroke="currentColor" strokeWidth="1.2" fill="none"/>
    <line x1="8" y1="9.5" x2="15" y2="9.5" stroke="currentColor" strokeWidth="1"/>
    <path d="M 10.5 9.5 L 10.5 11 L 12.5 11 L 12.5 9.5" stroke="currentColor" strokeWidth="1" fill="none"/>
  </svg>
);

/**
 * Unified mode icon (simple square)
 */
const UnifiedModeIcon = () => (
  <svg 
    width="16" 
    height="16" 
    viewBox="0 0 16 16" 
    fill="none" 
    xmlns="http://www.w3.org/2000/svg"
    style={{ display: 'inline-block', verticalAlign: 'middle' }}
  >
    {/* Simple square representing unified/all */}
    <rect x="4" y="4" width="8" height="8" rx="1" stroke="currentColor" strokeWidth="1.5" fill="none"/>
  </svg>
);

/**
 * @param {Object} props
 * @param {string} props.mode - Current mode: 'one' | 'two' | 'all'
 * @param {Function} props.onChange - Callback when mode changes (newMode) => void
 * @param {boolean} props.disabled - Whether selector is disabled
 * @param {string} props.label - Optional label text (default: empty, no label shown)
 * @param {Object} props.tooltips - Optional custom tooltip texts
 */
const ScreenSizeModeSelector = ({ 
  mode = 'one', 
  onChange, 
  disabled = false,
  label = '',
  tooltips = {
    one: 'One layout for all screen sizes',
    two: 'Separate layouts for mobile and desktop',
    all: 'Individual layouts for each breakpoint'
  }
}) => {
  const handleModeClick = (newMode) => {
    if (disabled || !onChange) return;
    onChange(newMode);
  };

  const buttonBaseClasses = `
    px-2 py-1 text-xs rounded border transition-colors
    flex items-center justify-center min-w-[28px]
  `;

  const getButtonClasses = (buttonMode) => {
    const isActive = mode === buttonMode;
    // NOTE: `surfaces.active` and `surfaces.tertiary` are both `bg-gray-100` in light mode,
    // so we add a subtle ring + focus border to make the selected mode unmistakable.
    const activeClasses = `${surfaces.active} ${borders.focus} ${textColors.primary} ring-2 ring-blue-200 dark:ring-blue-900/40 shadow-sm`;
    const inactiveClasses = `${surfaces.tertiary} ${borders.default} ${textColors.primary} ${surfaces.hover}`;
    const disabledClasses = 'opacity-50 cursor-not-allowed';
    
    return `
      ${buttonBaseClasses}
      ${isActive ? activeClasses : inactiveClasses}
      ${disabled ? disabledClasses : 'cursor-pointer'}
    `;
  };

  return (
    <div className="flex items-center justify-between mb-3">
      {label && (
        <label className={`${typography.label} ${textColors.secondary}`}>
          {label}
        </label>
      )}
      <div className={`flex gap-1 ${!label ? 'ml-auto' : ''}`}>
        {/* Unified Mode Button */}
        <button 
          type="button"
          onClick={() => handleModeClick('one')}
          disabled={disabled}
          title={tooltips.one}
          className={getButtonClasses('one')}
          aria-label={tooltips.one}
          aria-pressed={mode === 'one'}
        >
          <UnifiedModeIcon />
        </button>
        
        {/* Split Mode Button (Mobile + Desktop) */}
        <button 
          type="button"
          onClick={() => handleModeClick('two')}
          disabled={disabled}
          title={tooltips.two}
          className={getButtonClasses('two')}
          aria-label={tooltips.two}
          aria-pressed={mode === 'two'}
        >
          <SplitModeIcon />
        </button>
        
        {/* Custom Mode Button (All Breakpoints) */}
        <button 
          type="button"
          onClick={() => handleModeClick('all')}
          disabled={disabled}
          title={tooltips.all}
          className={getButtonClasses('all')}
          aria-label={tooltips.all}
          aria-pressed={mode === 'all'}
        >
          <MoreOutlined />
        </button>
      </div>
    </div>
  );
};

export default ScreenSizeModeSelector;

